/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import {
  IServiceMutationsProps,
  IServiceQueryProps,
} from '../interfaces/IHttpsServices';

/** Application headers */
const headers: HeadersInit = {
  Authorization: '',
  RequestVerificationToken: '',
  'Content-type': 'application/json; charset=UTF-8',
  'Accept-Language': i18next.language ?? 'en-US',
  'Strict-Transport-Security': 'max-age=63072000; includeSubDomains; preload',
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'SAMEORIGIN',
  'Content-Security-Policy': `default-src 'self'`
};

const setAuthHeader = (token: any): void => {
  headers.Authorization = token ? `Bearer ${token}` : '';
};

const takeUrl = (url: string, params?: object, hasCulture?: boolean) => {
  if (hasCulture && params) {
    return url + '?' + new URLSearchParams({ ...params });
  }
  if (params) {
    return (
      url +
      '?' +
      new URLSearchParams({ ...params, CultureCode: i18next.language })
    );
  }
  return url;
};

/** Fetch response validation */
const isValidFetchResponse = (res: Response): boolean => {
  const status: number = res.status;
  return res.ok && status >= 200 && status < 300;
};

/** Checks if it's a valid response or if it returns an error handler */
const responseAPI = async (res: Response) => {
  if (isValidFetchResponse(res)) {
    return res.json();
  } else {
    if (res.status === 422) {
      const errorMsg = await res.text().then((text) => JSON.parse(text));
      throw {
        status: res.status,
        message: errorMsg,
      };
    }
    throw res.status;
  }
};

/** onSuccess function */
const doSuccess = (
  success: unknown,
  successCallbackFunc?: (...arg: unknown[]) => void,
) => {
  /** @TODO - Missing conditionally success toast */
  successCallbackFunc?.(success);
};

/** onError function */
const doError = (
  error: any,
  errorCallbackFunc?: (...arg: unknown[]) => void,
) => {
  /** @TODO - Missing conditionally error toast */
  errorCallbackFunc?.(error);
  /** @TODO - Rever a necessidade de disparar esse erro */
  // throw new Error(error[API_ERROR_MESSAGE_KEY] || ERROR_MESSAGE_DEFAULT);
};

/** Function used in mutable CRUD services as CREATE / UPDATE / DELETE */
const useServiceData = ({
  url,
  method,
  data,
  errorCallbackFunc,
  successCallbackFunc,
}: IServiceMutationsProps) => {
  const mutations = useMutation({
    mutationFn: (body?: any) =>
      fetch(url, {
        headers,
        method,
        body: body ? JSON.stringify(body) : data,
      })
        .then((res) => responseAPI(res))
        .then(({ response }) => response),
    onSuccess: (success) => doSuccess(success, successCallbackFunc),
    onError: (error) => doError(error, errorCallbackFunc),
  });
  return mutations;
};

/** Function used to read data as READ */
const useGet = ({
  url,
  key,
  enabled,
  successCallbackFunc,
  errorCallbackFunc,
  params,
  hasCulture = false,
  retry = true,
}: IServiceQueryProps) => {
  const queries = useQuery({
    queryKey: key,
    queryFn: () =>
      fetch(takeUrl(url, params, hasCulture), {
        headers: { ...headers, 'Accept-Language': i18next.language ?? 'en-US' },
        method: 'GET',
      })
        .then((res) => responseAPI(res))
        .then(({ response }) => response),
    onSuccess: (success) => doSuccess(success, successCallbackFunc),
    onError: (error) => doError(error, errorCallbackFunc),
    enabled,
    refetchOnWindowFocus: false,
    retry,
  });
  return queries;
};

const useGetNoCache = ({
  url,
  enabled,
  successCallbackFunc,
  errorCallbackFunc,
  params,
  hasCulture = false,
  retry = true,
}: IServiceQueryProps) => {
  const queries = useQuery({
    queryFn: () =>
      fetch(takeUrl(url, params, hasCulture), {
        headers,
        method: 'GET',
      })
        .then((res) => responseAPI(res))
        .then(({ response }) => response),
    onSuccess: (success) => doSuccess(success, successCallbackFunc),
    onError: (error) => doError(error, errorCallbackFunc),
    enabled,
    refetchOnWindowFocus: false,
    retry,
  });
  return queries;
};

const useGetGlobalization = ({
  url,
  key,
  successCallbackFunc,
  errorCallbackFunc,
  params,
  hasCulture = false,
  retry = true,
}: IServiceQueryProps) => {
  const queryClient = useQueryClient();

  const queries = useQuery({
    queryKey: key,
    queryFn: () =>
      fetch(takeUrl(url, params, hasCulture), {
        headers: { ...headers, 'Accept-Language': i18next.language ?? 'en-US' },
        method: 'GET',
      })
        .then((res) => responseAPI(res))
        .then(({ response }) => response),
    onSuccess: (success) => doSuccess(success, successCallbackFunc),
    onError: (error) => doError(error, errorCallbackFunc),
    enabled: !queryClient.getQueryData(key),
    refetchOnWindowFocus: false,
    retry,
    cacheTime: 60 * 60 * 1000, // Set cache time to 60 minutes TASK #99187,
  });
  return queries;
};

/** Function used to update data as UPDATE */
const usePost = ({
  url,
  data,
  errorCallbackFunc,
  successCallbackFunc,
}: IServiceMutationsProps) =>
  useServiceData({
    url,
    method: 'POST',
    data,
    errorCallbackFunc,
    successCallbackFunc,
  });

/** Function used to create data as CREATE */
const usePut = ({
  url,
  data,
  errorCallbackFunc,
  successCallbackFunc,
}: IServiceMutationsProps) =>
  useServiceData({
    url,
    method: 'PUT',
    data,
    errorCallbackFunc,
    successCallbackFunc,
  });

/** Function used to delete data as DELETE */
const useRemove = ({
  url,
  data,
  errorCallbackFunc,
  successCallbackFunc,
}: IServiceMutationsProps) =>
  useServiceData({
    url,
    method: 'DELETE',
    data,
    errorCallbackFunc,
    successCallbackFunc,
  });

export const httpServices = {
  get: useGet,
  getNoCache: useGetNoCache,
  getGlobalization: useGetGlobalization,
  post: usePost,
  put: usePut,
  remove: useRemove,
  setAuthHeader,
};
